import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { fetchInvoices, downloadInvoice, downloadInvoiceAsPDF, sendInvoiceEmail } from '../../../services/invoiceService';
import { useAuth } from '../../../contexts/AuthContext';
import { toast } from '../../../components/ui/Toast';

const PaymentHistory = ({ subscription }) => {
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadInvoices = async () => {
      if (subscription?.tier === 'free') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const invoices = await fetchInvoices();
        setPayments(invoices);
      } catch (err) {
        console.error('Failed to load invoices:', err);
        setError(err?.message);
      } finally {
        setLoading(false);
      }
    };

    loadInvoices();
  }, [subscription?.tier]);

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: { text: 'Paid', color: 'text-green-500 bg-green-500/10' },
      open: { text: 'Open', color: 'text-orange-500 bg-orange-500/10' },
      draft: { text: 'Draft', color: 'text-gray-500 bg-gray-500/10' },
      void: { text: 'Void', color: 'text-red-500 bg-red-500/10' },
      uncollectible: { text: 'Uncollectible', color: 'text-red-500 bg-red-500/10' }
    };
    return badges?.[status] || badges?.paid;
  };

  const handleDownloadInvoice = async (invoice) => {
    setDownloading(invoice?.id);
    try {
      await downloadInvoice(invoice?.invoicePdf || invoice?.hostedInvoiceUrl, invoice?.id);
    } catch (err) {
      console.error('Failed to download invoice:', err);
      toast?.error('Failed to download invoice');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadPDF = async (invoice) => {
    setDownloading(invoice?.id);
    try {
      downloadInvoiceAsPDF(invoice);
      toast?.success('Invoice PDF downloaded successfully');
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      toast?.error('Failed to generate PDF');
    } finally {
      setDownloading(null);
    }
  };

  const handleEmailInvoice = async (invoice) => {
    setSendingEmail(invoice?.id);
    try {
      const { error } = await sendInvoiceEmail(user?.id, invoice);
      if (error) throw error;
      toast?.success('Invoice sent to your email');
    } catch (err) {
      console.error('Failed to send invoice email:', err);
      toast?.error('Failed to send invoice email');
    } finally {
      setSendingEmail(null);
    }
  };

  const calculateTotals = () => {
    const totalPaid = payments?.reduce((sum, payment) => sum + (payment?.amount || 0), 0) || 0;
    const firstPaymentDate = payments?.length > 0 
      ? new Date(payments[payments.length - 1]?.date)
      : new Date();
    
    return {
      totalPaid: totalPaid?.toFixed(2),
      invoiceCount: payments?.length || 0,
      memberSince: firstPaymentDate?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    };
  };

  const isFree = subscription?.tier === 'free';

  if (isFree) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
          <Icon name="Receipt" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2 font-heading">
          No Payment History
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          You're currently on the Free plan. Upgrade to access payment history and invoices.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
          <Icon name="Loader2" size={32} className="text-muted-foreground animate-spin" />
        </div>
        <p className="text-sm text-muted-foreground">Loading payment history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-red-500/10 mx-auto mb-4 flex items-center justify-center">
          <Icon name="AlertCircle" size={32} className="text-red-500" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2 font-heading">
          Failed to Load Payment History
        </h3>
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
      </div>
    );
  }

  if (payments?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
          <Icon name="Receipt" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2 font-heading">
          No Invoices Yet
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Your payment history will appear here once you have invoices.
        </p>
      </div>
    );
  }

  const totals = calculateTotals();
  const latestPaymentMethod = payments?.[0]?.paymentMethod || 'N/A';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-1 font-heading">
              Payment History
            </h2>
            <p className="text-sm text-muted-foreground">
              View and download your past invoices
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="CreditCard" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{latestPaymentMethod}</span>
          </div>
        </div>
      </div>
      {/* Payment List */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30 border-b border-border">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">Date</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">Description</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">Amount</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">Status</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {payments?.map((payment) => {
                const statusBadge = getStatusBadge(payment?.status);
                return (
                  <tr key={payment?.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-6 py-4 text-sm text-foreground">
                      {formatDate(payment?.date)}
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">{payment?.description}</p>
                        <p className="text-xs text-muted-foreground">{payment?.paymentMethod}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-foreground">
                      ${payment?.amount?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge?.color}`}>
                        {statusBadge?.text}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadPDF(payment)}
                          disabled={downloading === payment?.id}
                          iconName="FileText"
                          iconPosition="left"
                          title="Download PDF"
                        >
                          {downloading === payment?.id ? 'Generating...' : 'PDF'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEmailInvoice(payment)}
                          disabled={sendingEmail === payment?.id}
                          iconName="Mail"
                          iconPosition="left"
                          title="Email Invoice"
                        >
                          {sendingEmail === payment?.id ? 'Sending...' : 'Email'}
                        </Button>
                        {(payment?.invoicePdf || payment?.hostedInvoiceUrl) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadInvoice(payment)}
                            disabled={downloading === payment?.id}
                            iconName="Download"
                            iconPosition="left"
                            title="View Stripe Invoice"
                          >
                            Stripe
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Summary */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Total Paid</p>
            <p className="text-2xl font-bold text-foreground">${totals?.totalPaid}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Invoices</p>
            <p className="text-2xl font-bold text-foreground">{totals?.invoiceCount}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Member Since</p>
            <p className="text-2xl font-bold text-foreground">{totals?.memberSince}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistory;